import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { blogPosts, blogCategories } from '../data/blogPosts';

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = React.useState('all');
  
  const filteredPosts = activeCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category.toLowerCase() === activeCategory.toLowerCase());
  
  const featuredPosts = blogPosts.filter(post => post.featured);
  
  // Get display name for active category
  const getActiveCategoryName = () => {
    if (activeCategory === 'all') return 'All';
    const category = blogCategories.find(c => c.slug.toLowerCase() === activeCategory.toLowerCase());
    return category ? category.name : activeCategory;
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-[#1a1c1b] to-[#302f2c]">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="max-w-3xl">
            <span className="text-[#888680] text-sm font-medium uppercase tracking-widest">
              Resources & Guides
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-[#d9fb06] uppercase leading-[0.9] mt-4 mb-6">
              Blog
            </h1>
            <p className="text-lg text-[#888680] leading-relaxed">
              Expert guides on social media account appeals, platform policies, and documentation strategies. Learn how to organize your case materials.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-[#302f2c] border-y border-[#3f4816]/30">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="flex flex-wrap gap-3">
            {blogCategories.map((category) => (
              <button
                key={category.slug}
                onClick={() => setActiveCategory(category.slug)}
                className={`px-5 py-2 rounded-full text-sm font-medium uppercase tracking-wide transition-all ${
                  activeCategory === category.slug
                    ? 'bg-[#d9fb06] text-[#1a1c1b]'
                    : 'bg-[#1a1c1b] text-[#888680] hover:text-[#d9fb06] border border-[#3f4816]'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      {activeCategory === 'all' && featuredPosts.length > 0 && (
        <section className="py-16 bg-[#1a1c1b]">
          <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
            <h2 className="text-2xl font-bold text-[#d9fb06] uppercase mb-8">Featured Articles</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredPosts.slice(0, 3).map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group bg-[#302f2c] rounded-2xl p-6 border border-[#3f4816]/30 hover:border-[#d9fb06]/50 transition-all"
                >
                  <span className="inline-block px-3 py-1 bg-[#3f4816]/30 text-[#d9fb06] text-xs font-medium uppercase rounded-full mb-4">
                    {post.category}
                  </span>
                  <h3 className="text-xl font-bold text-[#dfddd6] group-hover:text-[#d9fb06] transition-colors mb-3 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-[#888680] text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-[#888680]">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {post.readTime}
                      </span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#d9fb06] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Posts */}
      <section className="py-16 bg-[#302f2c]">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <h2 className="text-2xl font-bold text-[#d9fb06] uppercase mb-8">
            {activeCategory === 'all' ? 'All Articles' : `${getActiveCategoryName()} Articles`}
          </h2>
          <div className="space-y-6">
            {filteredPosts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group block bg-[#1a1c1b] rounded-2xl p-6 border border-[#3f4816]/30 hover:border-[#d9fb06]/50 transition-all"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="inline-block px-3 py-1 bg-[#3f4816]/30 text-[#d9fb06] text-xs font-medium uppercase rounded-full">
                        {post.category}
                      </span>
                      <span className="text-xs text-[#888680]">{post.readTime}</span>
                    </div>
                    <h3 className="text-lg font-bold text-[#dfddd6] group-hover:text-[#d9fb06] transition-colors mb-2">
                      {post.title}
                    </h3>
                    <p className="text-[#888680] text-sm line-clamp-2">
                      {post.excerpt}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-[#d9fb06] font-medium text-sm">
                    Read Article
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1a1c1b]">
        <div className="max-w-3xl mx-auto px-5 md:px-10 text-center">
          <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase mb-4">
            Need Help With Your Account?
          </h2>
          <p className="text-[#888680] mb-8">
            Don't spend weeks trying to figure it out alone. Get a free case review and learn your options.
          </p>
          <Link
            to="/#intake"
            className="inline-flex items-center gap-2 bg-[#d9fb06] text-[#1a1c1b] px-8 py-4 rounded-full font-bold uppercase hover:bg-[#d9fb06]/90 transition-colors"
          >
            Get Free Case Review
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
